import React, { useMemo, useState } from "react";
import { Button, Dropdown, Input, Popover, Select, Table, Tag, Tooltip } from "antd";
import { ExportOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import urls from "../../../../urls";
import { usePurchaseOrderShipments } from "../../../../data/hooks";
import { formatPriceByCurrency } from "../../../../functions/formatters";
import { matchesShipmentSearch, trackingDisplay } from "../../../../functions/shipmentList";
import { ListCard, Num, PageHeader, RecordLink } from "../../../../components/shared-components/purchase";
import {
  PURCHASE_ORDER_SHIPMENT_SELECTABLE_STATUSES,
  PURCHASE_ORDER_SHIPMENT_STATUS_COLORS,
  PURCHASE_ORDER_SHIPMENT_STATUS_LABELS,
} from "../../../../constants/OrderConstant";

// Eén bron voor kleur en label (constants/OrderConstant.js).
const statusColors = PURCHASE_ORDER_SHIPMENT_STATUS_COLORS;
const statusLabels = PURCHASE_ORDER_SHIPMENT_STATUS_LABELS;

const PurchaseOrderShipmentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State declarations must come before useEffect hooks that use them
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  React.useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrderShipmentsFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      } else {
        // Default: hide cancelled and completed shipments
        setStatusFilter("active");
      }
    } catch (error) {
      console.error("Error initializing filters:", error);
      // Fallback to default values if localStorage fails
      setStatusFilter("active");
      setSupplierFilter(null);
      setCurrentPage(1);
    }
  }, []);

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    try {
      const filters = {
        supplierFilter,
        statusFilter,
        currentPage,
      };
      localStorage.setItem("purchaseOrderShipmentsFilters", JSON.stringify(filters));
    } catch (error) {
      console.warn("Failed to save filters to localStorage:", error);
      // Silently fail - filters will work but won't persist
    }
  }, [supplierFilter, statusFilter, currentPage]);

  // Use hook to fetch shipments
  const { shipments, loading } = usePurchaseOrderShipments();

  // Sync filters to URL
  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  // Get unique suppliers for filter
  const suppliers = [
    ...new Map(shipments.map((s) => [s.supplierId, { id: s.supplierId, name: s.supplierName }])).values(),
  ];

  // Apply filters
  const filteredShipments = shipments.filter((s) => {
    if (supplierFilter && s.supplierId !== supplierFilter) return false;
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "active") {
        // Hide cancelled and completed shipments
        if (s.status === "cancelled" || s.status === "completed") return false;
      } else {
        if (s.status !== statusFilter) return false;
      }
    }
    // Zoek op zendingnummer, container én de gekoppelde ordernummers — dat zijn
    // de drie dingen waarmee iemand een zending in de praktijk terugzoekt.
    return matchesShipmentSearch(s, search);
  });

  const applySearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
    updateSearchParams({ q: value, page: null });
  };

  // Kerncijfers voor de subtitel: laat zien waar aandacht nodig is voordat de
  // gebruiker de tabel in duikt.
  const summary = useMemo(() => {
    const drafts = shipments.filter((s) => s.status === "draft").length;
    const inTransit = shipments.filter((s) => s.status === "shipped").length;
    return { total: shipments.length, drafts, inTransit };
  }, [shipments]);

  const columns = [
    {
      // Datum vooraan (BMX-20): zendingen worden in de praktijk op "wanneer" teruggezocht.
      title: t("date"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (sec) =>
        sec ? (
          // Datum en tijd onder elkaar: de datum is waarop gesorteerd wordt, de
          // tijd is bijzaak maar helpt om zendingen van dezelfde dag te scheiden.
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Num>{dayjs.unix(sec).format("DD-MM-YYYY")}</Num>
            <Num size={11} muted>
              {dayjs.unix(sec).format("HH:mm")}
            </Num>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: t("shipment.shipment"),
      dataIndex: "shipmentNumber",
      key: "shipmentNumber",
      render: (value, record) => <RecordLink to={`${urls.purchaseOrderShipmentDetail}${record.id}`}>{value}</RecordLink>,
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      // Vaste minimumbreedte: leveranciersnamen braken eerder af over drie regels
      // omdat de andere kolommen de ruimte opslokten.
      render: (name) => <div style={{ minWidth: 200 }}>{name || "-"}</div>,
    },
    {
      title: "Container",
      dataIndex: "containerNumber",
      key: "containerNumber",
      render: (value) =>
        value ? (
          // Klikken zoekt op dit containernummer: zo zie je in één keer alle
          // zendingen die in dezelfde container zitten.
          <Tooltip title="Toon alle zendingen in deze container">
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={(e) => {
                e.stopPropagation();
                applySearch(value);
              }}
            >
              <Num>{value}</Num>
            </Button>
          </Tooltip>
        ) : (
          <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColors[status] || "default"}>{statusLabels[status] || status}</Tag>,
    },
    {
      title: t("order.purchase_orders"),
      key: "sourceOrders",
      render: (_, record) => {
        const orders = record.sourceOrders || [];
        if (!orders.length) return <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>;
        const [first, ...rest] = orders;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <RecordLink to={`${urls.purchaseOrdersList}/${first.purchaseOrderId}`}>
              {first.purchaseOrderNumber}
            </RecordLink>
            {rest.length ? (
              // Een zending kan regels uit zeven orders bundelen. Alle nummers naast
              // elkaar maakte de rij onleesbaar; de rest zit achter "+ n".
              <Popover
                trigger="click"
                placement="bottomLeft"
                title={`${orders.length} inkooporders`}
                content={
                  <div style={{ display: "flex", flexDirection: "column", maxWidth: 220 }}>
                    {orders.map((so) => (
                      <RecordLink
                        key={so.purchaseOrderId}
                        to={`${urls.purchaseOrdersList}/${so.purchaseOrderId}`}
                        style={{ padding: "5px 0" }}
                      >
                        {so.purchaseOrderNumber}
                      </RecordLink>
                    ))}
                  </div>
                }
              >
                <Button size="small" onClick={(e) => e.stopPropagation()}>
                  + {rest.length}
                </Button>
              </Popover>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Track & trace",
      dataIndex: "trackingCode",
      key: "trackingCode",
      render: (value) => {
        const tracking = trackingDisplay(value);
        if (!tracking) return <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>;
        // Een URL van ~110 tekens maakte de kolom onleesbaar breed. We tonen alleen
        // de domeinnaam; de volledige waarde staat in de tooltip.
        return tracking.url ? (
          <a
            href={tracking.url}
            target="_blank"
            rel="noopener noreferrer"
            title={tracking.url}
            // Anders opent de rij-klik óók de zending in dit tabblad.
            onClick={(e) => e.stopPropagation()}
            style={{ whiteSpace: "nowrap" }}
          >
            {tracking.label} <ExportOutlined style={{ fontSize: 11 }} />
          </a>
        ) : (
          <Num
            title={tracking.label}
            style={{ display: "inline-block", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "bottom" }}
          >
            {tracking.label}
          </Num>
        );
      },
    },
    {
      title: t("total_price"),
      dataIndex: "totalPrice",
      key: "totalPrice",
      align: "right",
      render: (v, row) => <Num>{v ? formatPriceByCurrency(row.totalPrice, row.currency) : "-"}</Num>,
    },
    {
      key: "actions",
      width: 44,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "open", label: t("shipment.shipment") + " openen" },
              ...(record.sourceOrders || []).slice(0, 1).map((so) => ({
                key: so.purchaseOrderId,
                label: `Inkooporder ${so.purchaseOrderNumber}`,
              })),
            ],
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === "open") navigate(`${urls.purchaseOrderShipmentDetail}${record.id}`);
              else navigate(`${urls.purchaseOrdersList}/${key}`);
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("purchase_order_shipments")}
        subtitle={`${summary.total} zendingen · ${summary.drafts} concept · ${summary.inTransit} onderweg`}
      />

      <ListCard
        filters={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "rgba(0, 0, 0, 0.35)" }} />}
              placeholder="Zending, container of inkooporder"
              style={{ width: 260 }}
              value={search}
              onChange={(e) => applySearch(e.target.value)}
            />
            <Select
              allowClear
              placeholder={t("supplier.supplier")}
              style={{ minWidth: 200 }}
              value={supplierFilter}
              onChange={(v) => {
                setSupplierFilter(v);
                setCurrentPage(1);
                updateSearchParams({ supplier: v, page: null });
              }}
              options={suppliers
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((s) => ({ value: s.id, label: s.name || s.id }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ minWidth: 240 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                updateSearchParams({ status: v, page: null });
              }}
              options={[
                { value: "active", label: "Actief (zonder geannuleerd/afgerond)" },
                // Kiesbare statussen, niet alle bekende: "Onderweg" is eruit (BMX-14).
                ...PURCHASE_ORDER_SHIPMENT_SELECTABLE_STATUSES.map((value) => ({
                  value,
                  label: statusLabels[value],
                })),
              ]}
            />
          </>
        }
        footnote="Ordernummers zijn links, geen tags: een tag zegt alleen iets over de status."
      >
        <Table
          loading={loading}
          dataSource={filteredShipments}
          columns={columns}
          scroll={{ x: true }}
          rowKey="id"
          pagination={{
            current: currentPage,
            showSizeChanger: false,
            onChange: (page) => {
              setCurrentPage(page);
              updateSearchParams({ page: String(page) });
            },
          }}
          onRow={(record) => ({
            onClick: () => navigate(`${urls.purchaseOrderShipmentDetail}${record.id}`),
            style: { cursor: "pointer" },
          })}
        />
      </ListCard>
    </>
  );
};

export default PurchaseOrderShipmentsList;
